import { queryOptions, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ApplicationDataType } from '@/components/contacts/application-list/application-list-item';
import useFriends from '@/hooks/contacts/use-friends';
import { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import { type GroupJoinApplication, GroupJoinMethod, groupJoinApplicationScheme } from '@/models/group/group-info';
import {
  generateListResponseScheme,
  type ListRespondedWith,
  type RespondedType,
  type ResponseError,
} from '@/models/response';
import { addToast } from '@/stores/toast';
import { splitBy } from '@/utils/array';
import { fetcher } from '@/utils/fetcher';
import { translateComplexKey } from '@/utils/translate';

export const generateGroupJoinApplicationQueryOptions = (groupId: number | null | undefined) =>
  queryOptions({
    queryKey: ['/GetGroupJoinApplications', groupId],
    queryFn: groupId
      ? async ({ signal }) => {
          const response = await fetcher<ListRespondedWith<GroupJoinApplication>>(
            '/GetGroupJoinApplications',
            { GroupId: groupId },
            { signal, schema: generateListResponseScheme(groupJoinApplicationScheme), validResponseCodes: [0, 1] },
          );

          if (!response.Data) return [];

          return response.Data.map<ApplicationDataType>((d) => ({
            id: d.ID,
            userId: d.UserID,
            status: d.Status === 0 ? 'pending' : d.Status === 1 ? 'accepted' : 'rejected',
            remark: generateJoinApplicationRemark(d.JoinMethod, d.Source),
            userName: d.UserNickName,
            avatarUrl: d.UserHeadImg,
            createTime: d.CreateTime,
          }));
        }
      : skipToken,
    placeholderData: [],
    staleTime: 15 * 1000,
  });

export default function useGroupJoinApplications(groupId: number | null | undefined) {
  const { t } = useTranslation();
  const { friends } = useFriends();

  return useQuery({
    ...generateGroupJoinApplicationQueryOptions(groupId),
    select: (data) => {
      const applications = data.map((d) => {
        const friend = friends[d.id];
        d.remark = translateComplexKey(d.remark, t);
        d.userName = friend?.RemarkName || d.userName;
        return d;
      });

      const [pendingApplications, pastApplications] = splitBy(applications, (d) => d.status === 'pending');
      return {
        applications: pendingApplications,
        pastApplications,
      };
    },
  });
}

export function useRespondGroupJoinApplication(groupId: Nullable<number>, application: ApplicationDataType) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, 'accept' | 'reject'>({
    mutationKey: ['/ProcessGroupJoinApplication', groupId, application.id],
    mutationFn: (params) =>
      fetcher(
        '/ProcessGroupJoinApplication',
        {
          GroupID: groupId,
          ApplicationID: application.id,
          Status: params === 'accept' ? '1' : '2',
        },
        { validResponseCodes: [0] },
      ),
    onSuccess: () =>
      Promise.allSettled([
        queryClient.invalidateQueries(generateGroupInfoQueryOptions(groupId)),
        queryClient.refetchQueries(generateGroupJoinApplicationQueryOptions(groupId)),
      ]),
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });
}

export function generateJoinApplicationRemark(
  method: GroupJoinMethod | string | undefined,
  remark: Nullable<string>,
  defaultMessage?: string,
): string | undefined {
  if (!method) return undefined;
  switch (method) {
    case GroupJoinMethod.ByLiveFansGroup:
    case 'ByLiveFansGroup':
      return remark ? `||${remark}` : undefined;
    case GroupJoinMethod.ByQrCode:
    case 'ByQrCode':
      return 'conversation.group.join-by-qr-code';
    case GroupJoinMethod.ByInvitation:
    case 'ByInvitation':
      return `conversation.group.join-by-invitation|user=${remark}`;
    case GroupJoinMethod.ByOneStation:
    case 'ByOneStation':
      return 'conversation.group.join-by-registration';
    default:
      return defaultMessage ? `||${defaultMessage}` : undefined;
  }
}
